import { FunctionComponent, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { CAMPAIGN_ROUTING_MAX_RULES, CampaignRoutingSettings } from "@signalist/shared-types";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";
import { Modal } from "@/components/Modals/Modal";
import { toastFailure } from "@/utils/toastFailure";
import { useSingleFlight } from "@/utils/useSingleFlight";

import { useAppSelector } from "@/redux/hooks";

import { useSaveCampaignRoutingMutation } from "@/pages/Private/redux/campaignRouting/campaignRouting.api";
import { selectCampaignRoutingSaving } from "@/pages/Private/redux/campaignRouting/campaignRouting.slice";

import { ROUTING_I18N } from "./utils/campaignRoutingFields";
import {
	canAddRule,
	draftCondition,
	DraftRule,
	emptyRule,
	isDraftSaveable,
	moveRule,
	RoutingDraft,
	toDraft,
	toSaveBody,
} from "./utils/campaignRoutingDraft";
import { RoutingCampaign } from "./utils/campaignRoutingTargets";
import { RoutingFallbackPicker } from "./RoutingFallbackPicker";
import { RoutingRuleCard } from "./RoutingRuleCard";

interface CampaignRoutingEditorProps {
	listId: number;
	/** The server's routing, read once when the editor mounts (web §24b: mounted per open). */
	settings: CampaignRoutingSettings;
	campaigns: readonly RoutingCampaign[];
	/** Whether the list's "Auto-assign to campaign" is on: decides what saving means for leads. */
	autoAssignEnabled: boolean;
	/** A save of this list is still in flight: show that, with no rules to edit and no Save. */
	waitsForSave?: boolean;
	onClose: () => void;
}

/**
 * The "Campaign routing rules" editor (PREC-4219, mockup frames 2 and 3). The host mounts it only
 * while open and keys it per opening, so every open starts from the server's state. Save replaces
 * the whole set in one PUT and runs once (web §24c). Cancel, Escape and the backdrop close it even
 * while the PUT is pending; the answer then changes nothing here (late answers, web §23), and a
 * refusal goes to the global toast, which outlives the dialog. Opened while that save is still in
 * flight, it waits (`waitsForSave`) and the host mounts it again once the save has answered; and it
 * never sends a PUT while another one for the list is in flight (one save per list at a time).
 */
export const CampaignRoutingEditor: FunctionComponent<CampaignRoutingEditorProps> = ({
	listId,
	settings,
	campaigns,
	autoAssignEnabled,
	waitsForSave = false,
	onClose,
}) => {
	const { t } = useTranslation();
	const [saveRouting] = useSaveCampaignRoutingMutation();
	// Any editor's save of this list, the one this editor started included (`run` drops those).
	const saveInFlight = useAppSelector(selectCampaignRoutingSaving(listId));
	const { run, isRunning } = useSingleFlight();
	const [draft, setDraft] = useState<RoutingDraft>(() => toDraft(settings));
	const [saveError, setSaveError] = useState(false);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;

		return () => {
			mountedRef.current = false;
		};
	}, []);

	const setRules = (update: (rules: DraftRule[]) => DraftRule[]) =>
		setDraft((current) => ({ ...current, rules: update(current.rules) }));

	const changeRule = (uid: string, next: DraftRule) =>
		setRules((rules) => rules.map((rule) => (rule.uid === uid ? next : rule)));

	const addCondition = (uid: string) =>
		setRules((rules) =>
			rules.map((rule) =>
				rule.uid === uid ? { ...rule, conditions: [...rule.conditions, draftCondition()] } : rule
			)
		);

	const save = () =>
		run(async () => {
			if (saveInFlight) {
				return;
			}

			setSaveError(false);

			try {
				await saveRouting({ listId, body: toSaveBody(draft) }).unwrap();
			} catch (error) {
				if (mountedRef.current) {
					setSaveError(true);
				} else {
					toastFailure(error, t(`${ROUTING_I18N}.editor.saveFailed`));
				}

				return;
			}

			if (mountedRef.current) {
				onClose();
			}
		});

	const defaultCampaign = campaigns.find((campaign) => campaign.isDefault);

	return (
		<Modal
			takeFocus
			disableSave={!isDraftSaveable(draft)}
			handleClose={onClose}
			handleSave={waitsForSave ? undefined : save}
			isLoading={isRunning}
			isOpened={true}
			size="lg"
			submitButtonText={t(`${ROUTING_I18N}.editor.save`)}
			subtitle={t(`${ROUTING_I18N}.editor.subtitle`)}
			title={t(`${ROUTING_I18N}.editor.title`)}
		>
			{waitsForSave ? (
				<p className="text-sm text-text-meta" data-testid="routing-editor-waiting">
					{t(`${ROUTING_I18N}.editor.savePending`)}
				</p>
			) : (
				<div className="flex flex-col gap-4" data-testid="routing-editor">
					<p className="text-sm text-text-meta" data-testid="routing-enrollment-note">
						{t(`${ROUTING_I18N}.editor.${autoAssignEnabled ? "enrollmentOn" : "enrollmentOff"}`)}
					</p>
					{saveError && (
						<div
							className="rounded border border-status-error bg-status-error-light p-2 text-sm text-status-error"
							role="alert"
						>
							{t(`${ROUTING_I18N}.editor.saveFailed`)}
						</div>
					)}
					<div className="flex flex-col gap-3">
						{draft.rules.map((rule, index) => (
							<RoutingRuleCard
								campaigns={campaigns}
								count={draft.rules.length}
								index={index}
								key={rule.uid}
								listId={listId}
								rule={rule}
								onAddCondition={() => addCondition(rule.uid)}
								onChange={(next) => changeRule(rule.uid, next)}
								onMove={(delta) => setRules((rules) => moveRule(rules, index, delta))}
								onRemove={() =>
									setRules((rules) => rules.filter((other) => other.uid !== rule.uid))
								}
							/>
						))}
					</div>
					<RoutingFallbackPicker
						defaultCampaign={defaultCampaign}
						fallback={draft.fallback}
						onChange={(fallback) => setDraft((current) => ({ ...current, fallback }))}
					/>
					<div className="flex items-center gap-2">
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							disabled={!canAddRule(draft)}
							size={ButtonSize.S}
							testId="routing-add-rule"
							title={t(`${ROUTING_I18N}.editor.addRule`)}
							onClick={() => setRules((rules) => [...rules, emptyRule()])}
						/>
						{!canAddRule(draft) && (
							<span className="text-xs text-text-meta" data-testid="routing-rules-limit">
								{t(`${ROUTING_I18N}.editor.limitRules`, { max: CAMPAIGN_ROUTING_MAX_RULES })}
							</span>
						)}
					</div>
				</div>
			)}
		</Modal>
	);
};
